import React, { useState } from 'react';
import {
  Gauge, Lightning, Drop, Factory, CurrencyDollar,
  Leaf, Target, Warning, TrendUp, TrendDown, MagnifyingGlass
} from '@phosphor-icons/react';
import OeeDrilldownPanel from './OeeDrilldownPanel';

function fmtVnd(n) {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n?.toLocaleString?.() ?? n;
}

function OeeGauge({ value, target, label }) {
  const pct = Math.min(value / 100, 1);
  const targetPct = Math.min(target / 100, 1);
  const color = value >= target ? '#4ade80' : value >= target - 5 ? '#facc15' : '#ef4444';
  const radius = 54;
  const circumference = Math.PI * radius;
  const offset = circumference * (1 - pct);

  return (
    <div className="flex flex-col items-center" data-testid={`oee-gauge-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <svg width="130" height="78" viewBox="0 0 130 78">
        <path d="M 11 72 A 54 54 0 0 1 119 72" fill="none" stroke="#2a2a2a" strokeWidth="10" strokeLinecap="round" />
        <path d="M 11 72 A 54 54 0 0 1 119 72" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
        <line
          x1={65 + radius * Math.cos(Math.PI * (1 - targetPct))}
          y1={72 - radius * Math.sin(Math.PI * (1 - targetPct))}
          x2={65 + (radius - 14) * Math.cos(Math.PI * (1 - targetPct))}
          y2={72 - (radius - 14) * Math.sin(Math.PI * (1 - targetPct))}
          stroke="#888" strokeWidth="2" strokeDasharray="3,2"
        />
        <text x="65" y="62" textAnchor="middle" className="font-heading" fill="white" fontSize="22" fontWeight="700">{value}%</text>
      </svg>
      <span className="text-xs font-body text-text-secondary mt-1">{label}</span>
      <span className="text-[10px] font-body text-text-secondary">Target: {target}%</span>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color = 'bg-primary/10', textColor = 'text-primary', trend, metricKey, onClick }) {
  return (
    <button
      onClick={() => onClick(metricKey, label, value)}
      data-testid={`oee-kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}
      className="text-left bg-surface border border-border rounded-lg p-4 hover:border-primary/50 hover:shadow-md transition-all duration-200 group cursor-pointer w-full"
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center shrink-0`}>
          <Icon size={18} weight="duotone" className={textColor} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-body font-semibold uppercase tracking-[0.12em] text-text-secondary truncate">{label}</p>
            <MagnifyingGlass size={12} className="text-text-secondary opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-1" />
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="font-heading text-xl font-bold text-text-primary">{value}</p>
            {trend && (
              trend === 'up'
                ? <TrendUp size={14} className="text-emerald-400" weight="bold" />
                : <TrendDown size={14} className="text-red-400" weight="bold" />
            )}
          </div>
          {sub && <p className="text-xs font-body text-text-secondary mt-0.5">{sub}</p>}
        </div>
      </div>
    </button>
  );
}

export default function OeeExecutive({ data }) {
  const [drilldown, setDrilldown] = useState(null);

  if (!data?.kpis) return null;
  const k = data.kpis;
  const s = data.initiative_summary;

  const gapOee = (k.target_oee_pct - k.plant_oee_pct).toFixed(1);
  const gapCost = (k.cost_per_good_unit_vnd - k.target_cost_per_unit_vnd).toLocaleString();

  const openDrilldown = (metricKey, label, value) => {
    setDrilldown({ metricKey, label, value });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="oee-executive">
      {/* OEE Gauges */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h2 className="font-heading text-base font-semibold text-text-primary mb-5">Plant OEE Breakdown</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 justify-items-center">
          <OeeGauge value={k.plant_oee_pct} target={k.target_oee_pct} label="Overall OEE" />
          <OeeGauge value={k.availability_pct} target={92} label="Availability" />
          <OeeGauge value={k.performance_pct} target={94} label="Performance" />
          <OeeGauge value={k.quality_pct} target={98} label="Quality" />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs font-body">
          <span className="flex items-center gap-1.5">
            <Warning size={14} className="text-amber-400" weight="fill" />
            <span className="text-text-secondary">Gap to target:</span>
            <span className="text-amber-400 font-semibold">{gapOee} pp</span>
          </span>
          <span className="text-border">|</span>
          <span className="flex items-center gap-1.5">
            <Target size={14} className="text-primary" weight="duotone" />
            <span className="text-text-secondary">Margin at risk:</span>
            <span className="text-red-400 font-semibold">{fmtVnd(k.margin_at_risk_vnd_month)} VND/mo</span>
          </span>
        </div>
      </div>

      {/* Clickable hint */}
      <p className="text-xs font-body text-text-secondary flex items-center gap-1.5">
        <MagnifyingGlass size={12} className="text-primary" />
        Click any tile below to drill down into line-by-line breakdown and AI-powered insights
      </p>

      {/* KPI Grid — Clickable Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={Factory} label="Good Units Today" value={k.good_units_today?.toLocaleString()} sub={`${k.total_units_today?.toLocaleString()} total produced`} color="bg-emerald-500/10" textColor="text-emerald-400" metricKey="good_units_today" onClick={openDrilldown} />
        <KpiCard icon={CurrencyDollar} label="Cost / Good Unit" value={`${fmtVnd(k.cost_per_good_unit_vnd)} VND`} sub={`Target: ${fmtVnd(k.target_cost_per_unit_vnd)} VND (gap: ${gapCost})`} color="bg-amber-500/10" textColor="text-amber-400" metricKey="cost_per_good_unit" onClick={openDrilldown} />
        <KpiCard icon={Lightning} label="Energy Today" value={`${k.energy_consumption_kwh_today?.toLocaleString()} kWh`} sub={`${k.peak_energy_pct}% during peak tariff`} color="bg-yellow-500/10" textColor="text-yellow-400" metricKey="energy_today" onClick={openDrilldown} />
        <KpiCard icon={Leaf} label="CO2e / Good Unit" value={`${k.co2e_per_good_unit_kg} kg`} sub={`Energy cost: ${fmtVnd(k.energy_cost_per_good_unit_vnd)} VND/unit`} color="bg-teal-500/10" textColor="text-teal-400" metricKey="co2e_per_good_unit" onClick={openDrilldown} />
        <KpiCard icon={Drop} label="Water Today" value={`${k.water_consumption_m3_today} m3`} sub={`${k.water_per_good_unit_l} L per good unit`} color="bg-blue-500/10" textColor="text-blue-400" metricKey="water_today" onClick={openDrilldown} />
        <KpiCard icon={Warning} label="Scrap Rate" value={`${k.scrap_rate_pct}%`} sub={`${k.total_units_today - k.good_units_today} units scrapped`} color="bg-red-500/10" textColor="text-red-400" metricKey="scrap_rate" onClick={openDrilldown} />
        <KpiCard icon={Lightning} label="Utility Downtime" value={`${k.utility_downtime_min_today} min`} sub="Energy / utility related stoppages" color="bg-orange-500/10" textColor="text-orange-400" metricKey="utility_downtime" onClick={openDrilldown} />
        <KpiCard icon={Gauge} label="Initiatives Impact" value={`+${s?.total_oee_points || 0} pp`} sub={`${fmtVnd(s?.total_vnd_saved_day || 0)} VND/day saved`} color="bg-violet-500/10" textColor="text-violet-400" trend="up" metricKey="initiatives_impact" onClick={openDrilldown} />
      </div>

      {/* Initiative Summary Strip */}
      <div className="bg-surface border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-heading text-sm font-semibold text-text-primary">Initiative Portfolio</h3>
            <p className="text-xs font-body text-text-secondary mt-0.5">{s?.total || 0} initiatives across Energy, Utilities, Quality, Safety & Supply Chain</p>
          </div>
          <div className="flex gap-4 text-center">
            {[
              { label: 'Completed', count: s?.completed, color: 'text-emerald-400' },
              { label: 'In Progress', count: s?.in_progress, color: 'text-amber-400' },
              { label: 'Planned', count: s?.planned, color: 'text-blue-400' },
            ].map(({ label, count, color }) => (
              <div key={label}>
                <p className={`font-heading text-lg font-bold ${color}`}>{count || 0}</p>
                <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">{label}</p>
              </div>
            ))}
            <div className="border-l border-border pl-4">
              <p className="font-heading text-lg font-bold text-text-primary">{s?.realization_pct || 0}%</p>
              <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">Realized</p>
            </div>
          </div>
        </div>
      </div>

      {/* Drilldown Panel */}
      {drilldown && (
        <OeeDrilldownPanel
          metricKey={drilldown.metricKey}
          metricLabel={drilldown.label}
          metricValue={drilldown.value}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
}
